/**
 * Headless presentation helpers for fulfilment routes on the dictionary
 * admin surface (product brief
 * docs/features/catalogue-third-party-fulfilment/01_product_brief.md §8).
 * The cost matrix's cells — one key, four marks — are in
 * cost-matrix-cells.ts; these are the drill-down's, the route summary's,
 * the index's and the overnight banner's.
 *
 * House rule: cost is what we pay, price is what we charge — everything
 * here is a cost.
 */

import type {
  FailedMappingLeaf,
  RouteCoverage,
  SupplierCostFreshness,
  SupplierFetchAttempt,
  SupplierRequestAttribute,
  SupplierRouteUpkeep,
} from "../data/fulfilment-route-types";

export type RouteTone = "success" | "warning" | "error" | "neutral";

/** A supplier cost's freshness as the drill-down's badge names it. */
export const SUPPLIER_COST_FRESHNESS: Record<
  SupplierCostFreshness,
  { label: string; tone: RouteTone }
> = {
  live: { label: "Live", tone: "success" },
  expiring: { label: "Expiring", tone: "warning" },
  expired: { label: "Expired", tone: "error" },
  not_yet_quoted: { label: "Not yet quoted", tone: "neutral" },
  unmapped: { label: "Unmapped", tone: "neutral" },
  errored: { label: "Errored last night", tone: "error" },
};

/** A fetch attempt's outcome, as the cost-pair page's fetch history names
 *  it: a quote came back, Solopress refused the request, or the call failed
 *  (timeout, outage) and will be retried. */
export const ATTEMPT_OUTCOME: Record<
  SupplierFetchAttempt["outcome"],
  { label: string; tone: RouteTone }
> = {
  succeeded: { label: "Quote returned", tone: "success" },
  rejected: { label: "Rejected", tone: "error" },
  failed_transient: { label: "Failed", tone: "error" },
};

/** Carriage is shown as-is in phase 1 (#27): stated wherever the two
 *  figures sit side by side, so nobody reads the difference as like-for-like. */
export const DELIVERY_INCLUDED_NOTE =
  "Solopress quotes include UK mainland delivery; our costs never include carriage, so the two are not yet like-for-like.";

/** #37: the difference is labelled with the side carriage sits on. */
export function formatDifferenceLabel(supplierLabel: string): string {
  return `Difference (delivery included on the ${supplierLabel} side)`;
}

// ── Mappings ────────────────────────────────────────────────────────────────
// Mappings are code with no lifecycle (Ben, 24 Sep): a leaf either resolves
// or failed last night's run. Errors are the only signal.

/** The item row's sub-row for what a back-end requires that our item does
 *  not vary (phase 1: Solopress turnaround, and colours where the item has
 *  no colour field). */
export const FIXED_ON_ITEM_LABEL = "Fixed on this item — no option to choose";

/** Where one attribute of a supplier request came from on our side, in the
 *  item tree's own words: "Item", "Stock: 350gsm silk", the fixed sub-row,
 *  or the quantity ladder. */
export function formatRequestAttributeSource(
  attribute: SupplierRequestAttribute,
): string {
  switch (attribute.sourceKind) {
    case "item":
      return "Item";
    case "fixed_on_item":
      return FIXED_ON_ITEM_LABEL;
    case "quantity":
      return "Quantity ladder rung";
    case "option":
      return `${attribute.fieldLabel ?? ""}: ${attribute.optionLabel ?? ""}`;
  }
}

/** "Stock → 120gsm woven" — a failed leaf's place in its item's tree. */
export function formatMappingLeafPath(path: string[]): string {
  return path.join(" → ");
}

/** 'Letterheads · Solopress: Stock → 120gsm woven — "120gsm Bond" not in
 *  product list'. */
export function formatFailedMappingLeaf(leaf: FailedMappingLeaf): string {
  return `${leaf.dictionaryItemName} · ${leaf.backEnd.label}: ${formatMappingLeafPath(leaf.path)} — ${leaf.message}`;
}

/** "No mapping errors last night" / "1 mapping errored last night". */
export function formatErroredLeafCount(count: number): string {
  if (count === 0) return "No mapping errors last night";
  return `${count} ${count === 1 ? "mapping" : "mappings"} errored last night`;
}
/** "1 of 2 variants can be mapped (50%)". */
export function formatVariantResolution(
  resolved: number,
  total: number,
): string {
  const pct = total === 0 ? 0 : Math.round((resolved / total) * 100);
  return `${resolved} of ${total} ${total === 1 ? "variant" : "variants"} can be mapped (${pct}%)`;
}

// ── Route coverage (index) ──────────────────────────────────────────────────

export function isBelowRouteTarget(coverage: RouteCoverage): boolean {
  return coverage.availableRouteCount < coverage.target;
}

export function formatRouteCount(count: number): string {
  return `${count} ${count === 1 ? "route" : "routes"}`;
}

// ── Populate status banner ──────────────────────────────────────────────────

/** Decided by the backend (`get_warm_status`, including the 24-hour
 *  rule); the page only words it. */
export type SupplierUpkeepState = SupplierRouteUpkeep["state"];

export const SUPPLIER_UPKEEP_STATES: Record<
  SupplierUpkeepState,
  { label: string; tone: RouteTone }
> = {
  healthy: { label: "Up to date", tone: "success" },
  partial_failure: { label: "Some quotes failed", tone: "warning" },
  not_run_recently: { label: "Not run in the last 24 hours", tone: "error" },
  not_configured: { label: "Not set up", tone: "neutral" },
};

/** "35 min" / "7 h" / "3 days" old. */
export function formatAge(iso: string, now: Date): string {
  const minutes = Math.max(
    0,
    Math.round((now.getTime() - new Date(iso).getTime()) / 60_000),
  );
  if (minutes < 60) return `${minutes} min old`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h old`;
  return `${Math.round(hours / 24)} days old`;
}

/** "35 min ago" / "7 h ago" / "3 days ago". */
export function formatAgo(iso: string, now: Date): string {
  return formatAge(iso, now).replace(/ old$/, " ago");
}
